import { queryOptions, useQuery } from '@tanstack/react-query';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

export interface ParentsBulkImportWarning {
  success: true;
  parentEmail: string;
  parentId?: string;
  parentFirstName?: string;
  parentLastName?: string;
  studentFirstName: string;
  studentLastName: string;
  createdStudentId?: string;
  existingStudentId?: string;
  rowHint?: string;
  reason: string;
  message: string;
  manualResolutionKey: string;
}

export interface ParentsBulkImportDuplicateUnresolved {
  success: false;
  failureReason: string;
  parentEmail: string;
  parentId?: string;
  parentFirstName?: string;
  parentLastName?: string;
  studentFirstName: string;
  studentLastName: string;
  existingStudentId?: string;
  rowHint?: string;
  reason: string;
  manualResolutionKey: string;
}

export interface ParentsBulkImportRowOutcome {
  rowKey: string;
  parentEmail: string;
  success: boolean;
  failureReason?: string;
}

export interface ParentsBulkImportJobResult {
  success: number;
  failed: number;
  errors: string[];
  batchId: string | null;
  duplicateUnresolved: ParentsBulkImportDuplicateUnresolved[];
  importWarnings?: ParentsBulkImportWarning[];
  parentsCreated?: number;
  parentsReused?: number;
  studentsCreated?: number;
  studentsSkipped?: number;
  /** Rows with parent email that duplicated an earlier row's email (merged) */
  sameParentEmailMergedRowCount?: number;
  rowOutcomes?: ParentsBulkImportRowOutcome[];
  duplicateSignalsCount?: number;
}

export type ParentsBulkImportJobOutcome =
  | 'completed'
  | 'completed_with_errors'
  | 'failed'
  | 'cancelled'
  | 'processing'
  | 'queued';

export interface ParentsBulkImportJob {
  jobId: string;
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled';
  totalRecords: number;
  processedRecords: number;
  /** Completed count, or current parent index while that parent is in-flight. */
  activeParentIndex?: number;
  currentParentEmail: string | null;
  currentParentIndex?: number | null;
  progress: number;
  result?: ParentsBulkImportJobResult | null;
  errorMessage: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  correlationId?: string | null;
  importContentHash?: string | null;
  processingStartedAt?: string | null;
  durationMs?: number | null;
  partialOutcome?: string | null;
  outcome?: ParentsBulkImportJobOutcome;
}

export interface ParentsBulkImportStatusResponse {
  success: boolean;
  job: ParentsBulkImportJob;
}

export interface GetParentsBulkImportStatusParams {
  jobId: string;
  /** Poll every N ms while the progress modal is open (0 = disabled). */
  refetchIntervalMs?: number;
}

export const getParentsBulkImportStatusAPI = async (
  params: GetParentsBulkImportStatusParams,
) => {
  const response = await baseAPI.get<ParentsBulkImportStatusResponse>(
    `/parents/bulk-import-status/${encodeURIComponent(params.jobId)}`,
  );
  return response.data;
};

export const getParentsBulkImportStatusQueryOptions = (
  params: GetParentsBulkImportStatusParams,
) =>
  queryOptions({
    queryKey: [KEYS.PARENTS_BULK_IMPORT_STATUS, params.jobId],
    queryFn: () => getParentsBulkImportStatusAPI(params),
    enabled: !!params.jobId,
    staleTime: 0,
    refetchIntervalInBackground: true,
    refetchInterval:
      params.refetchIntervalMs && params.refetchIntervalMs > 0
        ? params.refetchIntervalMs
        : false,
  });

export const useGetParentsBulkImportStatus = (
  params: GetParentsBulkImportStatusParams,
) => {
  return useQuery(getParentsBulkImportStatusQueryOptions(params));
};
